import { useDispatch } from 'react-redux'
import { isNil, isNumber } from 'lodash'
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { ClipCreatorMarkType } from 'types/preview'
import { setActivePreview, setInOutPointsMoving } from 'actions/preview'
import { MARK_TYPE } from 'config/constants/preview'
import { stopPlaybackClip, stopPlaybackTimeline } from 'actions/playback'
import { LocalClipCreatorMarkerType, UseEditClip } from './types'
import { INITIAL_MOUSE_MOVEMENT_DATA } from './constant'

export const useEditClip = ({
  preview,
  activePreview,
  onSetMarker,
  clipCreatorMarkers,
  duration,
  previewPlayerVideoElem,
  onRewind,
  progress,
  mouseMovementData,
  setLocalMarkers,
  setDragging,
  ioPointsBarRef,
  isMediaServerProcessing,
  isDragging,
  isMedia,
}: UseEditClip) => {
  const dispatch = useDispatch()
  const [ domKey, setDomKey ] = useState(0)
  // Only for media server player. When isMediaServerProcessing will be false.
  useMemo(() => {
    if (activePreview === preview) {
      const { savedProgress, markType: isMoving } = mouseMovementData.current
      if (!isMediaServerProcessing && !isMoving && !isDragging && isNumber(savedProgress)) {
        onRewind(savedProgress)
        // eslint-disable-next-line no-param-reassign
        mouseMovementData.current = { ...INITIAL_MOUSE_MOVEMENT_DATA }
      }
    }
  }, [ isMediaServerProcessing, mouseMovementData, onRewind, activePreview, preview, isDragging ])

  const onClickMarkIcon = useCallback((
    markType: Exclude<ClipCreatorMarkType, 'clearInOutPoints'>,
    isResizer
  ) => (e: MouseEvent) => {
    if (activePreview !== preview) dispatch(setActivePreview(preview))
    if (isResizer) {
      dispatch(isMedia ? stopPlaybackClip() : stopPlaybackTimeline())
      dispatch(setInOutPointsMoving(true))
    }
    // eslint-disable-next-line no-param-reassign
    mouseMovementData.current = { ...mouseMovementData.current, markType, left: e.clientX, savedProgress: progress }
    setLocalMarkers(() => ({ ...clipCreatorMarkers }))
  }, [ preview, progress, mouseMovementData, clipCreatorMarkers, setLocalMarkers, dispatch, activePreview, isMedia ])

  const onMouseUp = useCallback(e => {
    if (preview === activePreview) {
      setDragging(prev => {
        if (isNumber(mouseMovementData.current.savedProgress)) {
          if (preview === activePreview && prev) {
            onRewind(mouseMovementData.current.savedProgress ?? 0)
          }
        }
        return false
      })
      document.body.style.cursor = 'default'
      if (mouseMovementData.current.markType) {
        if (isNumber(mouseMovementData.current.savedProgress) && !isMediaServerProcessing) {
          onRewind(mouseMovementData.current.savedProgress ?? 0)
        }
        setLocalMarkers({} as LocalClipCreatorMarkerType)
      }
      /*
       * Only for media server player.
       * if isMediaServerProcessing true don't remove savedProgress for media player preview.
      */
      if (isMediaServerProcessing) {
        // eslint-disable-next-line no-param-reassign
        mouseMovementData.current = {
          ...INITIAL_MOUSE_MOVEMENT_DATA,
          savedProgress: mouseMovementData.current.savedProgress,
        }
      } else {
        // eslint-disable-next-line no-param-reassign
        mouseMovementData.current = { ...INITIAL_MOUSE_MOVEMENT_DATA }
      }
      dispatch(setInOutPointsMoving(false))
    }
  }, [
    onRewind,
    mouseMovementData,
    dispatch,
    setLocalMarkers,
    preview,
    activePreview,
    setDragging,
    isMediaServerProcessing,
  ])

  const { left, right } = useMemo(() => {
    if (previewPlayerVideoElem) {
      const rect = previewPlayerVideoElem.getBoundingClientRect()
      return {
        left: rect.left,
        right: rect.right,
      }
    }
    return {}
  // eslint-disable-next-line
  }, [ previewPlayerVideoElem, domKey ])

  useEffect(() => {
    const handleResize = () => setDomKey(prev => prev + 1)
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const onMouseMove = useCallback((e: globalThis.MouseEvent) => {
    if (preview === activePreview) {
      if (mouseMovementData.current.markType && !mouseMovementData.current.dragging) {
        e.stopPropagation()
        window.getSelection()?.removeAllRanges()
        document.body.style.cursor = 'ew-resize'
        if (!isNil(left) && !isNil(right)) {
          const pixelsInSecond = (right - left) / duration
          let newTimePoint = (e.clientX - left) / pixelsInSecond
          newTimePoint = e.clientX <= left ? 0 : newTimePoint
          newTimePoint = e.clientX >= right ? duration : newTimePoint
          onSetMarker(
            activePreview,
            mouseMovementData.current.markType
          )(e, { newTimePoint })
        }
      }

      if (mouseMovementData.current.dragging && ioPointsBarRef.current) {
        e.stopPropagation()
        window.getSelection()?.removeAllRanges()
        document.body.style.cursor = 'grabbing'
        if (!isNil(left) && !isNil(right)) {
          if (isNil(mouseMovementData.current.startX)) {
            // set initial x params
            const { left: leftBarX, right: rightBarX } = ioPointsBarRef.current.getBoundingClientRect()
            // eslint-disable-next-line no-param-reassign
            mouseMovementData.current = {
              ...mouseMovementData.current,
              startX: e.clientX,
              leftBarX,
              rightBarX,
            }
          }
          const pixelsInSecond = (right - left) / duration
          const offset = e.clientX - mouseMovementData.current.draggingX
          const offsetDraggedIOPointsSegment = offset / pixelsInSecond

          const isLeftMove = offset < 0
          const isRightMove = offset > 0

          const { leftBarX, rightBarX, startX } = mouseMovementData.current
          let isAllowed = true
          if (isNumber(leftBarX) && isNumber(rightBarX) && isNumber(startX)) {
            if (isRightMove) {
              // Diff between ioPointsBar leftX and center drag
              const diff = startX - leftBarX
              if ((e.clientX - diff) < left) {
                isAllowed = false
                // eslint-disable-next-line no-param-reassign
                mouseMovementData.current.draggingX = left + diff // set draggingX in the middle of ioPoints bar
              }
            } else if (isLeftMove) {
              // Diff between ioPointsBar rightX and center drag
              const diff = rightBarX - startX
              if ((e.clientX + diff) > right) {
                isAllowed = false
                // eslint-disable-next-line no-param-reassign
                mouseMovementData.current.draggingX = right - diff // set draggingX in the middle of ioPoints bar
              }
            }
          }
          if (isAllowed) {
            onSetMarker(
              activePreview,
              MARK_TYPE.DRAG
            )(e, { newTimePoint: null, offsetDraggedIOPointsSegment })
            // eslint-disable-next-line no-param-reassign
            mouseMovementData.current.draggingX = e.clientX
          }
        }
      }
    }
  }, [
    activePreview,
    onSetMarker,
    preview,
    duration,
    left,
    right,
    mouseMovementData,
    ioPointsBarRef,
  ])

  const onSetDragging = useCallback((dragging: boolean) => (e: MouseEvent) => {
    if (dragging) {
      dispatch(setActivePreview(preview))
      dispatch(isMedia ? stopPlaybackClip() : stopPlaybackTimeline())
      dispatch(setInOutPointsMoving(true))
      setDragging(dragging)
      // eslint-disable-next-line no-param-reassign
      mouseMovementData.current = {
        ...mouseMovementData.current,
        dragging,
        draggingX: dragging ? e.clientX : 0,
        savedProgress: dragging ? progress : null,
      }
    }
    if (!dragging) {
      document.body.style.cursor = 'default'
    }
  }, [ setDragging, mouseMovementData, progress, preview, dispatch, isMedia ])

  useEffect(() => {
    if (preview === activePreview) {
      window.addEventListener('mouseup', onMouseUp)
      document.addEventListener('mousemove', onMouseMove)
    }
    return () => {
      window.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('mousemove', onMouseMove)
    }
  }, [ onMouseUp, onMouseMove, activePreview, preview ])

  return {
    onClickMarkIcon,
    savedProgress: mouseMovementData.current.savedProgress,
    onSetDragging,
  }
}
